import { useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import SendInviteModal from "../../components/client/SendInviteModal";
import { createClientInvite } from "../../js/api/clientInvite";
import { useAuth } from "../../js/hooks/useAuth";
import { useDesk } from "../../js/hooks/useDesk";

const EMPTY = { kind: "buyer", name: "", email: "", phone: "", address: "", notes: "" };

export default function StartHereGuide() {
  const { session } = useAuth();
  const desk = useDesk();
  const variant = desk.variant === "broker" ? "broker" : "agent";
  const [form, setForm] = useState(EMPTY);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(null);

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function handleCreate(event) {
    event.preventDefault();
    setError("");
    setNote("");
    try {
      const row = createClientInvite({ session, ...form });
      setInvite(row);
      setNote(
        form.kind === "seller"
          ? "The seller invitation is ready. Their Client ID appears after they finish setup."
          : "The buyer invitation is ready. Their Client ID appears after they finish setup."
      );
      setForm(EMPTY);
    } catch (err) {
      setError(err.message || "We could not start that File yet.");
    }
  }

  return (
    <AppLayout variant={variant}>
      <article className="start-here">
        <div className="page-header">
          <div>
            <p className="eyebrow">A quiet first walk</p>
            <h1>New to REChecklist? Start Here.</h1>
            <p className="page-lead">
              Your Desk is the command center in the upper-right corner. Everything below is meant
              to feel like a companion — not a manual.
            </p>
          </div>
        </div>

        <section className="start-here__card">
          <h2>How to create a new buyer</h2>
          <p>
            Choose Buyer below and send a REChecklist invitation. Their search lives on its own
            carpet — it will never mix with a seller’s house. Their Client ID is created when they
            finish setup.
          </p>
        </section>

        <section className="start-here__card">
          <h2>How to create a new seller</h2>
          <p>
            Choose Seller instead and send the matching invitation. Their portfolio is a separate
            File. You can hold both kinds of clients on your Desk; the work itself stays apart.
          </p>
        </section>

        <form className="start-here__card start-here__form" onSubmit={handleCreate}>
          <h2>Enter client information</h2>
          <p>
            Use real names if you wish. The invitation email carries only the correct buyer or
            seller setup link.
          </p>
          <fieldset className="start-here__roles">
            <legend>This File is for</legend>
            <label className={form.kind === "buyer" ? "is-on" : ""}>
              <input
                type="radio"
                name="kind"
                value="buyer"
                checked={form.kind === "buyer"}
                onChange={update}
              />
              A buyer
            </label>
            <label className={form.kind === "seller" ? "is-on" : ""}>
              <input
                type="radio"
                name="kind"
                value="seller"
                checked={form.kind === "seller"}
                onChange={update}
              />
              A seller
            </label>
          </fieldset>
          <label className="field">
            <span className="field-label">Name</span>
            <input className="field-input" name="name" value={form.name} onChange={update} required />
          </label>
          <label className="field">
            <span className="field-label">Email</span>
            <input
              className="field-input"
              name="email"
              type="email"
              value={form.email}
              onChange={update}
              required
            />
          </label>
          <label className="field">
            <span className="field-label">Phone</span>
            <input className="field-input" name="phone" value={form.phone} onChange={update} />
          </label>
          <label className="field">
            <span className="field-label">Property address (optional)</span>
            <input className="field-input" name="address" value={form.address} onChange={update} />
          </label>
          <label className="field">
            <span className="field-label">Notes (optional)</span>
            <textarea className="field-input" name="notes" rows={3} value={form.notes} onChange={update} />
          </label>
          <button className="primary-btn" type="submit">
            Prepare invitation
          </button>
          {error ? <p className="start-here__error">{error}</p> : null}
          {note ? <p className="start-here__ok">{note}</p> : null}
        </form>

        <section className="start-here__card">
          <h2>How to send messages</h2>
          <p>
            Open Client Messaging from the left of your Desk. Write a calm note. If you are in the
            Beta Tester Program, you may mark a note as program feedback so the team can find it.
          </p>
          <p>
            <Link to={desk.messages}>Open Client Messaging</Link>
            {" · "}
            <Link to={desk.officeMessages}>Open Office Messaging</Link>
          </p>
        </section>

        <section className="start-here__card">
          <h2>Prospects</h2>
          <p>
            People who write in before they are clients live on Prospects — not on a buyer or
            seller File. Convert only when you are ready to open a journey.
          </p>
          <p>
            <Link to={desk.prospects}>Open Prospects</Link>
          </p>
        </section>

        <section className="start-here__card">
          <h2>The Hub</h2>
          <p>
            The Hub is a private room for brokers and agents only — not for clients. Share notes,
            office circles, and industry listings. Client files stay on your Desk.
          </p>
          <p>
            <Link to={`${desk.hub}/hall`}>Open The Hub</Link>
          </p>
        </section>

        <section className="start-here__card">
          <h2>How to access support</h2>
          <p>
            Support is a message away — in your messaging panel, or by writing to us. During Beta,
            replies may not always be immediate, but you can expect a note back within a short
            period of time.
          </p>
          <p>
            <Link to={desk.clients}>Client Manager</Link>
            {" · "}
            <a href="mailto:sysadmin@rechecklist.pro">sysadmin@rechecklist.pro</a>
            {" · "}
            <a href="mailto:support@rechecklist.pro">support@rechecklist.pro</a>
          </p>
        </section>
      </article>
      <SendInviteModal invite={invite} isOpen={Boolean(invite)} onClose={() => setInvite(null)} />
    </AppLayout>
  );
}
